import { api } from '@/utils/api';

type PageBody = { data?: unknown; pagination?: { total?: number; totalPages?: number } };
export type LoadAllOptions = {
  /** Plain-English name for the error message, e.g. 'Supplier'. */
  label: string;
  params?: Record<string, string | number>;
  /** Vendor selector rule: refuse a list that has no page counts. */
  requirePagination?: boolean;
};

/**
 * Load every row of a paged list endpoint by walking all pages (100 a page).
 * Never returns a partial list: if the list changes while loading, it throws.
 * A plain array reply (no page counts, e.g. the dispatch_manager picker) is already the full list.
 */
export async function loadAllPages<T = any>(path: string, signal: AbortSignal, opts: LoadAllOptions): Promise<T[]> {
  const changed = () => new Error(`${opts.label} list changed. Reload it to continue.`);
  const rows: T[] = [];
  let pages = 1, total = 0;
  for (let page = 1; page <= pages; page++) {
    if (signal.aborted) throw new Error(`${opts.label} loading cancelled`);
    const response = await api.get(path, { params: { ...opts.params, page, limit: 100 }, signal });
    const body = (response.data ?? {}) as PageBody;
    if (!Array.isArray(body.data)) throw new Error(`${opts.label} list is not readable`);
    const pg = body.pagination;
    if (!pg || typeof pg.total !== 'number' || typeof pg.totalPages !== 'number') {
      if (opts.requirePagination) throw new Error(`${opts.label} list is not readable`);
      return body.data as T[];
    }
    if (page === 1) { pages = pg.totalPages; total = pg.total; }
    if (pg.total !== total || pg.totalPages !== pages || (total > 0 && !body.data.length)) throw changed();
    rows.push(...(body.data as T[]));
  }
  const ids = rows.map((r: any) => r?.id).filter((id) => id !== undefined);
  if (rows.length !== total || new Set(ids).size !== ids.length) throw changed();
  return rows;
}
